<template>
  <div class="pdf-viewer">
    <div class="pdf-viewer_header">
      <div class="file-info">
        <div class="file-info__icon">
          <FilePreview :file="{name: fileName}" :sizes="{width:48, height:48}"/>
        </div>
        <div class="file-info__title-size-wrap">
          <div class="file-info--title">{{ fileName }}</div>
          <div class="file-info--size">{{ fileSize }}</div>
        </div>
        <div class="file-info--actions">
          <div v-if="downloadable" class="actions-btn download-btn" @click="onDownloadClick">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 3V17M12 17L9 14M12 17L15 14M8 10H5C3.895 10 3 10.895 3 12V19C3 20.105 3.895 21 5 21H19C20.105 21 21 20.105 21 19V12C21 10.895 20.105 10 19 10H16" stroke="#707C8C" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path>
            </svg>
          </div>
          <div v-if="removable" class="actions-btn remove-btn" @click="onRemoveClick">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18.5 6.5V19C18.5 19.5304 18.2893 20.0391 17.9142 20.4142C17.5391 20.7893 17.0304 21 16.5 21H7.5C6.96957 21 6.46086 20.7893 6.08579 20.4142C5.71071 20.0391 5.5 19.5304 5.5 19V6.5M9 17H15M9 13H15M8.5 6.5V4.5C8.5 4.10218 8.65804 3.72064 8.93934 3.43934C9.22064 3.15804 9.60218 3 10 3H14C14.3978 3 14.7794 3.15804 15.0607 3.43934C15.342 3.72064 15.5 4.10218 15.5 4.5V6.5M20 6.5H4" stroke="#707C8C" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path>
            </svg>
          </div>
        </div>
      </div>
    </div>
    <div class="pdf-viewer_body">
      <div class="arrows arrow_left arrows_left-container">
        <div v-if="currentPage > 1" class="arrow" @click.stop="onLeftArrowClick">
          <svg width="20" height="21" viewBox="0 0 20 21" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g clip-path="url(#clip0_21938_8768)">
              <path d="M15.8335 10.2499C16.1786 10.2499 16.4585 10.5297 16.4585 10.8749C16.4585 11.1913 16.2233 11.4528 15.9183 11.4942L15.8335 11.4999H3.3418C2.99662 11.4999 2.7168 11.22 2.7168 10.8749C2.7168 10.5585 2.95192 10.297 3.25699 10.2555L3.3418 10.2499H15.8335Z" fill="#1C1C1C"/>
              <path d="M7.90215 5.43968C8.1462 5.19559 8.54196 5.19556 8.78605 5.43961C9.00796 5.66148 9.02813 6.0087 8.84663 6.25339L8.78613 6.3235L4.2183 10.8916L8.78613 15.4597C9.00796 15.6816 9.02813 16.0288 8.84655 16.2735L8.78605 16.3436C8.56413 16.5654 8.2169 16.5856 7.97225 16.404L7.90215 16.3435L2.89299 11.3335C2.67114 11.1116 2.65097 10.7644 2.83248 10.5198L2.89299 10.4497L7.90215 5.43968Z" fill="#1C1C1C"/>
            </g>
            <defs>
              <clipPath id="clip0_21938_8768">
                <rect width="20" height="20" fill="white" transform="translate(0 0.891602)"/>
              </clipPath>
            </defs>
          </svg>
        </div>
      </div>
      <div ref="pageViewContainer" class="page-view-container">
        <div v-if="loaded" class="splider-container">
          <Splide
            ref="pageCarousel"
            has-slider-wrapper
            :options="splideOptions"
            :slides="docs"
            @splide:active="onSplideActive"
            @splide:move="onSlideMove"
          >
            <template v-for="(doc, index) in docs" :key="doc.name">
              <template v-if="isPdfDocument(doc)">
                <SplideSlide
                  v-for="(page, page_index) in doc.attachments"
                  :key="`${index}-${page_index}`"
                  :data-slide-index="`${index}-${page_index}`"
                >
                  <PDFPageViewer
                    :page="page"
                    :scale="page.scale"
                    @page-rendered="onPageRendered"
                    @page-render-error="onPageRenderError"
                  />
                </SplideSlide>
              </template>
              <SplideSlide v-else-if="isImageDocument(doc)" :data-slide-index="index">
                <div class="zoomer-container" style="width:100%; height:100%">
                  <VZoomer
                    :ref="($el: ZoomerComponentInstance) => bindRef($el, index)"
                    v-model:zoomed="zoomed"
                    :double-click-to-zoom="false"
                    :max-scale="10"
                    class="page-zoomer"
                    style="background-color: #F7F9FD; border-radius: 8px"
                  >
                    <img :src="doc.src" loading="lazy" @load="onImageLoad(index)">
                  </VZoomer>
                </div>
              </SplideSlide>
              <SplideSlide v-else :data-slide-index="index">
                <DocPlaceholder @download-file="onDownloadClick"/>
              </SplideSlide>
            </template>
          </Splide>
        </div>
        <div v-if="!loaded" class="preloader-container">
          <SPreloader />
        </div>
      </div>
      <div class="arrows arrow_right arrows_right-container">
        <div v-if="currentPage < totalPages" class="arrow" @click.stop="onRightArrowClick">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g clip-path="url(#clip0_21938_292)">
              <path d="M4.16654 9.35834C3.82137 9.35834 3.54154 9.63817 3.54154 9.98334C3.54154 10.2998 3.7767 10.5613 4.0817 10.6027L4.16654 10.6083H16.6582C17.0034 10.6083 17.2832 10.3285 17.2832 9.98334C17.2832 9.66692 17.0481 9.40542 16.743 9.364L16.6582 9.35834H4.16654Z" fill="#1C1C1C"/>
              <path d="M12.0978 4.54808C11.8538 4.30399 11.458 4.30395 11.214 4.54801C10.992 4.76988 10.9719 5.1171 11.1534 5.36179L11.2139 5.4319L15.7817 9.99999L11.2139 14.5681C10.992 14.79 10.9719 15.1372 11.1535 15.3819L11.214 15.452C11.4359 15.6738 11.7831 15.694 12.0278 15.5124L12.0978 15.4519L17.107 10.4419C17.3289 10.22 17.349 9.87282 17.1675 9.62815L17.107 9.55807L12.0978 4.54808Z" fill="#1C1C1C"/>
            </g>
            <defs>
              <clipPath id="clip0_21938_292">
                <rect width="20" height="20" fill="white" transform="matrix(-1 0 0 1 20 0)"/>
              </clipPath>
            </defs>
          </svg>

        </div>
      </div>
    </div>
    <div class="pdf-viewer_footer">
      <div v-if="actionsAvailable" class="actions">
        <button v-if="viewBtnAvailable" type="button" class="btn-open" @click="onViewBtnClick">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="24" height="24" rx="4" fill="#1C1C1C"/>
            <path d="M14.122 9.88C15.293 11.051 15.293 12.952 14.122 14.125C12.951 15.296 11.05 15.296 9.877 14.125C8.706 12.954 8.706 11.053 9.877 9.88C11.05 8.707 12.95 8.707 14.122 9.88Z" fill="white"/>
            <path d="M3 12C3 11.341 3.152 10.689 3.446 10.088C4.961 6.991 8.309 5 12 5C15.691 5 19.039 6.991 20.554 10.088C20.848 10.689 21 11.341 21 12C21 12.659 20.848 13.311 20.554 13.912C19.039 17.009 15.691 19 12 19C8.309 19 4.961 17.009 3.446 13.912C3.152 13.311 3 12.659 3 12Z" fill="white"/>
            <path d="M14.122 9.88C15.293 11.051 15.293 12.952 14.122 14.125C12.951 15.296 11.05 15.296 9.877 14.125C8.706 12.954 8.706 11.053 9.877 9.88C11.05 8.707 12.95 8.707 14.122 9.88Z" stroke="#1C1C1C" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M3 12C3 11.341 3.152 10.689 3.446 10.088C4.961 6.991 8.309 5 12 5C15.691 5 19.039 6.991 20.554 10.088C20.848 10.689 21 11.341 21 12C21 12.659 20.848 13.311 20.554 13.912C19.039 17.009 15.691 19 12 19C8.309 19 4.961 17.009 3.446 13.912C3.152 13.311 3 12.659 3 12Z" stroke="#1C1C1C" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <span>Просмотреть</span>
        </button>
        <div class="actions__paginator-zoom-wrap">
          <div class="pages" :class="{padding: !removable}">
            <span class="pages__page-text">Страница</span> {{ currentPage }} из {{ totalPages }}</div>
          <div class="zoom">
            <svg class="zoom-out" width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg" @click="onZoomOut">
              <g clip-path="url(#clip0_7637_136939)">
                <path d="M20.9994 23.3346H6.99935C5.71018 23.3346 4.66602 22.2905 4.66602 21.0013V7.0013C4.66602 5.71214 5.71018 4.66797 6.99935 4.66797H20.9994C22.2885 4.66797 23.3327 5.71214 23.3327 7.0013V21.0013C23.3327 22.2905 22.2885 23.3346 20.9994 23.3346Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path>
                <path d="M18.6673 14H9.33398" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path>
              </g>
              <defs>
                <clippath id="clip0_7637_136939">
                  <rect width="28" height="28" fill="currentColor"></rect>
                </clippath>
              </defs>
            </svg>
            <svg class="zoom-in" width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg" @click="onZoomIn">
              <g clip-path="url(#clip0_7637_136940)">
                <path d="M20.9993 3.79297H6.99935C5.22694 3.79297 3.79102 5.22889 3.79102 7.0013V21.0013C3.79102 22.7737 5.22694 24.2096 6.99935 24.2096H20.9993C22.7717 24.2096 24.2077 22.7737 24.2077 21.0013V7.0013C24.2077 5.22889 22.7717 3.79297 20.9993 3.79297ZM6.99935 5.54297H20.9993C21.8053 5.54297 22.4577 6.19538 22.4577 7.0013V21.0013C22.4577 21.8072 21.8053 22.4596 20.9993 22.4596H6.99935C6.19343 22.4596 5.54102 21.8072 5.54102 21.0013V7.0013C5.54102 6.19538 6.19343 5.54297 6.99935 5.54297Z" fill="currentColor"></path>
                <path d="M14 8.45703C14.443 8.45703 14.8091 8.78621 14.8671 9.2133L14.875 9.33203V18.6654C14.875 19.1486 14.4832 19.5404 14 19.5404C13.557 19.5404 13.1909 19.2111 13.1329 18.7841L13.125 18.6654V9.33203C13.125 8.84879 13.5168 8.45703 14 8.45703Z" fill="currentColor"></path>
                <path d="M18.6673 13.125C19.1506 13.125 19.5423 13.5168 19.5423 14C19.5423 14.443 19.2131 14.8091 18.7861 14.8671L18.6673 14.875H9.33398C8.85074 14.875 8.45898 14.4832 8.45898 14C8.45898 13.557 8.78816 13.1909 9.21525 13.1329L9.33398 13.125H18.6673Z" fill="currentColor"></path>
              </g>
              <defs>
                <clippath id="clip0_7637_136940">
                  <rect width="28" height="28" fill="currentColor"></rect>
                </clippath>
              </defs>
            </svg>
          </div>
        </div>
        <div class="actions__delete-download-wrap" >
          <SButton v-if="removable" size="small" variant="secondary" wide @click="onRemoveClick">Удалить документ</SButton>
          <SButton size="small" variant="primary" wide @click="onDownloadClick">Скачать файл</SButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, shallowRef, triggerRef, computed, watch, onMounted, onBeforeUnmount, onBeforeUpdate } from 'vue'

import { Splide, SplideSlide } from '@splidejs/vue-splide'
import { Splide as SplideInstance, SlideComponent } from '@splidejs/splide'
import '@splidejs/vue-splide/css'

import VueZoomer from 'vue-zoomer'
const VZoomer = VueZoomer.Zoomer

import * as pdfjs from 'pdfjs-dist'
import 'pdfjs-dist/build/pdf.worker.entry'
// @ts-ignore
pdfjs.GlobalWorkerOptions.workerSrc = pdfjsWorker

import SPreloader from '@components/preloader/Preloader.vue'
import PDFPageViewer from './PDFPageViewer.vue'
import DocPlaceholder from './DocPlaceholder.vue'
import FilePreview from "@components/file/FilePreview.vue";
import { formatFileSize } from '@components/uploader/helper'
import { isRemoteFile } from '@/types/uploader'
import type { UploaderFile } from '@/types/uploader'
import type { Document, ImageDocument, PdfDocument, PdfPage, ZoomerComponentInstance, SplideComponentInstance } from '@components/uploader/types'
import {SButton} from "@/components";

const props = defineProps({
  files: {
    type: Array<UploaderFile>,
    required: true
  },
  fileIndex: {
    type: Number,
    default: 0
  },
  imageFormats: {
    type: Array<string>,
    // default: () => ['apng', 'gif', 'ico', 'cur', 'jpg', 'jpeg', 'pjpeg', 'pjp', 'png', 'svg', 'webp', 'bmp']
    default: () => []
  },
  removable: {
    type: Boolean,
    default: true
  },
  downloadable: {
    type: Boolean,
    default: true
  }
})

const emit = defineEmits<{
  'remove-file': [index: number]
  'file-index-changed': [index: number]
  'page-rendered': [pageNumber: number]
  'page-render-error': [error: unknown]
}>()

const currentPage = ref(0)
const totalPages = ref(0)
const loaded = ref(false)
const zoomed = ref(false)
const fileName = ref('')
const fileSize = ref('')
const viewBtnAvailable = ref(false)
const actionsAvailable = ref(false)
const docs = shallowRef<Document[]>([])

const pageViewContainer = ref<Element | null>(null)
const pageCarousel = ref<SplideComponentInstance | null>(null)
const pageZoomer = ref<Record<number, ZoomerComponentInstance>>({})

const slideNumber = computed(() => {
  return [...docs.value].splice(0, props.fileIndex).reduce((acc, doc) => {
    acc += isPdfDocument(doc) ? doc.attachments.length : 1
    return acc
  }, 0)
})

function bindRef(el: ZoomerComponentInstance, docIdx: number) {
  if (el) {
    pageZoomer.value[docIdx] = el
  } else {
    delete pageZoomer.value[docIdx]
  }
}

onBeforeUpdate(() => {
  pageZoomer.value = {}
})

const splideOptions = computed(() => ({
  type: 'slide',
  width: '100%',
  start: slideNumber.value,
  height: getCarouselHeight(), // нереактивное значение
  breakpoints: {
    744: {
      drag: true
    }
  },
  perPage: 1,
  perMove: 1,
  pagination: false,
  arrows: false,
  drag: false,
  rewind: true
}))

watch(() => props.files, async (newFiles) => {
  loaded.value = false
  docs.value = await preloadFiles(newFiles)
  loaded.value = true
})

const getFileExtension = (filename: string) => {
  return filename.slice(((filename.lastIndexOf('.') - 1) >>> 0) + 2).toLowerCase()
}

const isPdfDocument = (doc: Document): doc is PdfDocument => getFileExtension(doc.name) === 'pdf'

const isImageDocument = (doc: Document): doc is ImageDocument => props.imageFormats.includes(getFileExtension(doc.name))

const getSlideDocIndex = (slide: SlideComponent): number => Number(slide.slide.getAttribute('data-slide-index')!.split('-')[0] || -1)

const getSlidePageIndex = (slide: SlideComponent): number => Number(slide.slide.getAttribute('data-slide-index')!.split('-')[1] || -1)

const getCarouselHeight = (): number => pageViewContainer.value?.clientHeight ?? 0

const preloadFiles = async (files: UploaderFile[]): Promise<Document[]> => {
  const docs: Document[] = []
  try {
    for (const file of files) {
      const url = isRemoteFile(file) ? file.link : URL.createObjectURL(file)
      if (getFileExtension(file.name) === 'pdf') {
        const preloaded_doc = await pdfjs.getDocument(url).promise as PdfDocument
        preloaded_doc.total_pages = preloaded_doc.numPages
        preloaded_doc.src = url
        preloaded_doc.scale = 1
        preloaded_doc.size = file.size
        preloaded_doc.name = file.name
        preloaded_doc.attachments = await fetchDocumentPages(preloaded_doc)

        docs.push(preloaded_doc)
      } else {
        const preloaded_img: ImageDocument = {
          name: file.name,
          total_pages: 1,
          size: file.size,
          src: url
        }
        docs.push(preloaded_img)
      }
    }
    return docs
  } catch (e: any) {
    console.warn(e?.message)
    return docs
  }
}

const onSplideActive = (splide: SplideInstance) => {
  updateInfo(splide)
}

const setPreviousDocument = (): Promise<number> => {
  return new Promise((resolve) => {
    const docIndex = getSlideDocIndex(getCurrentSlide()!)
    if (docIndex > 0) {
      const slides = getSplide()!.Components.Slides.get()
      const prev_doc = slides
        .filter((slide) => {
          const index = getSlideDocIndex(slide)
          return index < docIndex
        })
        .pop()
      emit('file-index-changed', prev_doc!.index)
    }
    resolve(docIndex)
  })
}

const fetchDocumentPages = (doc = getCurrentDocument() as PdfDocument): Promise<PdfPage[]> => {
  return new Promise((resolve, reject) => {
    if (!doc || !doc.numPages) resolve([])

    getPages(doc, 1, doc.numPages)
      .then((pages) => {
        const _pages: PdfPage[] = pages.map((item) => {
          // 96dpi / 72dpi
          item.scale = 1.33
          return item
        })
        resolve(_pages)
      })
      .catch((error) => {
        reject(error)
      })
  })
}

const getPages = (doc: PdfDocument, startPage = 0, endPage = 0): Promise<PdfPage[]> => {
  const pages: Promise<PdfPage>[] = []
  for (let i = startPage; i <= endPage; i++) {
    pages.push(doc.getPage(i) as Promise<PdfPage>)
  }
  return Promise.all(pages)
}

const onLeftArrowClick = () => {
  pageCarousel.value!.splide.go('<')
  emit('file-index-changed',  pageCarousel.value!.splide.index)
}

const onRightArrowClick = () => {
  pageCarousel.value!.splide.go('>')
  emit('file-index-changed',  pageCarousel.value!.splide.index)
}

const resize = () => {
  if (pageCarousel.value?.splide.options) {
    pageCarousel.value.splide.options = {
      height: `${getCarouselHeight()}px`
    }
  }
}

const onPageRendered = (pageNumber: number) => {
  emit('page-rendered', pageNumber)
}

const onPageRenderError = (error: unknown) => {
  emit('page-render-error', error)
}

// const resetPageScale = () => {
//   const slide = getCurrentSlide()!
//   const docIndex = getSlideDocIndex(slide)
//   const pageIndex = getSlidePageIndex(slide)
//   if (pageIndex >= 0) {
//     const doc = docs.value[docIndex] as PdfDocument
//     const page = doc.attachments[pageIndex]
//     if (page.scale !== 1) page.scale = 1
//     triggerRef(docs)
//   }
// }

const setPageScale = ({ docIndex, pageIndex, zoomIn }: { docIndex: number, pageIndex: number, zoomIn: boolean }) => {
  const doc = docs.value[docIndex] as PdfDocument
  const page = doc.attachments[pageIndex]

  if (zoomIn && page.scale <= 10) {
    page.scale = page.scale + 0.25
  } else if (!zoomIn && page.scale > 1) {
    page.scale = page.scale - 0.25
  }

  triggerRef(docs)
}

const onZoomIn = () => {
  const slide = getCurrentSlide()!
  const docIndex = getSlideDocIndex(slide)
  const pageIndex = getSlidePageIndex(slide)
  if (pageIndex >= 0) {
    setPageScale({ docIndex, pageIndex, zoomIn: true })
  } else {
    const zoomer = pageZoomer.value[docIndex]
    if (zoomer) zoomer.zoomIn()
  }
}

const onZoomOut = () => {
  const slide = getCurrentSlide()!
  const docIndex = getSlideDocIndex(slide)
  const pageIndex = getSlidePageIndex(slide)
  if (pageIndex >= 0) {
    setPageScale({ docIndex, pageIndex, zoomIn: false })
  } else {
    const zoomer = pageZoomer.value[docIndex]
    if (zoomer) zoomer.zoomOut()
  }
}

const onSlideMove = () => {
  // resetPageScale()
}

const onDownloadClick = () => {
  try {
    const doc = getCurrentDocument()

    if (!doc) return

    const anchor = document.createElement('a')

    anchor.href = doc.src
    anchor.download = doc.name

    document.body.appendChild(anchor)
    anchor.click()
    document.body.removeChild(anchor)
  } catch (error: any) {
    console.warn(error?.message ?? error)
  }
}

const onViewBtnClick = () => {
  const doc = getCurrentDocument()
  if (!doc) return
  window.open(doc.src, '_blank')
}

const onImageLoad = (index: number) => {
  try {
    pageZoomer.value[index].onWindowResize()
  } catch (error: any) {
    console.warn(error?.message)
  }
}

const onRemoveClick = async () => {
  const delDocIndex = await setPreviousDocument()
  emit('remove-file', delDocIndex)
}

const getSplide = () => {
  return pageCarousel.value?.splide ?? null
}

const getCurrentSlide = () => {
  const splide = getSplide()

  if (!splide) return null

  return splide.Components.Slides.getAt(splide.index) ?? null
}

const getCurrentDocument = (): Document | undefined => {
  const currentSlide = getCurrentSlide()

  if (!currentSlide) return

  const docIndex = getSlideDocIndex(currentSlide)
  return docs.value[docIndex]
}

const updateInfo = (splide: SplideInstance) => {
  const doc = getCurrentDocument()!
  fileName.value = doc.name
  fileSize.value = formatFileSize(doc.size)
  viewBtnAvailable.value = isPdfDocument(doc)
  actionsAvailable.value = isPdfDocument(doc) || isImageDocument(doc)
  totalPages.value = splide.length
  currentPage.value = splide.index + 1

}

const init = async () => {
  docs.value = await preloadFiles(props.files)
  const doc = docs.value[props.fileIndex]
  if (doc) {
    fileName.value = doc.name
    fileSize.value = formatFileSize(doc.size)
  }
  loaded.value = true
}

onMounted(async () => {
  await init()
  resize()
  window.addEventListener('resize', resize)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', resize)
})
</script>

<style lang="scss" scoped>
@import '@/assets/styles/scss/mixins.scss';

.pdf-viewer {
  position: relative;
  display: flex;
  flex: 1 1 100%;
  overflow: hidden;
  flex-direction: column;

  &_header {
    font-size: 16px;
    font-weight: 400;
    color: var(--color-lbutton-light);
    display: flex;
    align-items: center;
    justify-content: flex-start;
    margin-bottom: 16px;
    padding: 0 16px;
    @include narrow_tablet {
      padding: 0 20px 24px;
      margin-bottom: 0;
      box-shadow: 0 0 10px 0 rgba(0,0,0, 0.05);
      border-radius: 0 0 12px 12px;
      z-index: 1;
    }
    @include phone {
      padding: 0 16px 16px;
    }

    .file-info {
      display: flex;
      align-items: center;
      flex: 1 1 auto;
      overflow: hidden;
      background-color: var(--color-bg-secondary);
      padding: 8px 12px;
      border-radius: 4px;
      &__icon {
        width: 48px;
        height: 48px;
        margin-right: 12px;
      }

      &__title-size-wrap {
        margin-right: auto;
      }

      &--title {
        flex: 1;
        overflow: hidden;
        text-overflow: ellipsis;
        line-height: 22px;
        color: var(--color-text-primary)
      }

      &--size {
        color: var(--color-icon-secondary);
      }

      &--actions {
        display: flex;
        height: 100%;
        align-items: center;
        gap: 12px;

        .actions-btn {
          cursor: pointer;
          &:last-child {
            margin-right: 0;
          }
        }
      }
    }

    @include narrow_tablet {
      font-size: 14px;
      .actions-btn {
        display: none;
      }

      .file-info {
        &--size {
          font-size: 14px;
          line-height: 20px;
        }
      }
    }
  }

  &_body {
    display: flex;
    overflow: hidden;
    position: relative;
    justify-content: center;
    height: 100%;
    width: 100%;
    padding: 0 16px 16px;

    @include narrow_tablet {
      padding: 0;
    }

    .page-view-container {
      width: 100%;
      height: 100%;

      .splider-container {
        width: 100%;
        height: 100%;
      }

      .preloader-container {
        display: flex;
        width: 100%;
        height: 100%;
        justify-content: center;
        align-items: center;
      }

      .zoomer-container {
        height: 100%;
        width: 100%;
        display: flex;
        justify-content: center;

        .page-zoomer {
          width: 100%;
          height: 100%;
          background-color: var(--color-bg-secondary);
          img {
            width: 100%;
            height: 100%;
            object-fit: scale-down;
            border-radius: 8px;
          }
        }
      }

      .image-viewer {
        border-radius: 8px;
        border: 1px solid var(--stroke-cards);
        overflow: hidden;

        img {
          max-width: 100%;
          max-height: 100%;
        }
      }
    }

    .arrows {
      position: absolute;
      display: flex;
      flex-direction: column;
      justify-content: center;
      padding: inherit;
      top: calc(50% - 22px);
      z-index: 2;

      &_left-container {
        left: 16px;
      }

      &_right-container {
        right: 16px;
      }

      .arrow {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 44px;
        height: 44px;
        border-radius: 100px;
        background-color: var(--color-bg-primary);
        border: 1px solid var(--stroke-cards);
        cursor: pointer;

        user-select: none;
        -moz-user-select: none;
        -webkit-user-select: none;
        -ms-user-select: none;
      }
    }

  }

  &_footer {
    display: flex;
    align-items: center;
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    padding-bottom: 16px;
    @include narrow_tablet {
      position: relative;
      padding: 20px;
      box-shadow: 0 -2px 6px 0 rgba(0,0,0, 0.05);
      z-index: 1;
    }
    @include phone {
      padding: 16px;
    }


    .actions {
      display: flex;
      align-items: center;
      padding-left: 16px;
      padding-right: 16px;
      flex: 1 1 100%;
      color: var(--color-text-primary);
      font-size: 16px;
      font-weight: 400;
      position: relative;
      @include narrow_tablet {
        padding: 0;
      }
      .pages {
        font-size: 14px;
        line-height: 20px;
        padding-right: 12px;
        &__page-text {
          @include narrow_tablet {
            display: none;
          }
        }
        @include narrow_tablet {
          padding-right: 0;
        }
      }

      .zoom {
        display: flex;
        justify-content: center;
        align-items: center;
        position: relative;
        padding-left: 12px;
        gap: 4px;
        &:before {
          content: '';
          display: block;
          width: 1px;
          height: 24px;
          background-color: #BABCC2;
          position: absolute;
          left: 0;
          top: 50%;
          transform: translateY(-50%);
        }

        &-label {
          margin-right: 14px;
        }

        &-in,
        &-out {
          cursor: pointer;
        }
        @include narrow_tablet {
          display: none;
        }
      }

      &__paginator-zoom-wrap {
        display: flex;
        align-items: center;
        padding: 4px 16px;
        background-color: var(--color-hint);
        color: var(--color-text-contrast);
        border-radius: 32px;
        margin-left: auto;

        @include narrow_tablet {
          position: absolute;
          bottom: calc(100% + 40px);
          right: 0;
        }
        @include phone {
          bottom: calc(100% + 26px);
        }
      }

      &__delete-download-wrap {
        display: none;
        @include narrow_tablet {
          display: flex;
          width: 100%;
          gap: 12px;
        }
        @include phone {
          flex-direction: column-reverse;
        }
      }
    }
  }
}
.btn-open {
  display: flex;
  gap: 8px;
  align-items: center;
  padding: 4px 12px;
  border-radius: 4px;
  border: 1px solid var(--stroke-select, #A0A2A8);
  font-size: 14px;
  line-height: 14px;
  font-weight: 500;
  color: var(--color-text-primary);
  background-color: var(--color-bg-primary);
  cursor: pointer;
  @include narrow_tablet {
    position: absolute;
    left: 0;
    bottom: calc(100% + 40px)
  }
  @include phone {
    left: 0;
    bottom: calc(100% + 26px)
  }
}
</style>
